import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPackage, FiChevronDown, FiChevronUp, FiTruck, FiCheck, FiStar } from 'react-icons/fi';
import api from '../services/api';
import './Orders.css';

const MOCK_ORDERS = [
  {
    id: 'MYN2024001', date: '2024-10-15', status: 'DELIVERED', total: 3798, paymentMethod: 'UPI',
    items: [
      { id: 1, name: 'Floral Print Maxi Dress', brand: 'Zara', price: 2499, imageEmoji: '👗', size: 'M', qty: 1 },
      { id: 2, name: 'Slim Fit Cotton Shirt', brand: 'H&M', price: 1299, imageEmoji: '👔', size: 'L', qty: 1 },
    ],
    address: { name: 'Priya Sharma', address: '123 MG Road, Bangalore - 560001' },
    deliveredDate: '2024-10-18',
  },
  {
    id: 'MYN2024002', date: '2024-10-20', status: 'SHIPPED', total: 4999, paymentMethod: 'CARD',
    items: [
      { id: 5, name: 'Running Sports Shoes', brand: 'Nike', price: 4999, imageEmoji: '👟', size: '9', qty: 1 },
    ],
    address: { name: 'Priya Sharma', address: '123 MG Road, Bangalore - 560001' },
    trackingId: 'TRK123456789',
  },
  {
    id: 'MYN2024003', date: '2024-10-22', status: 'PROCESSING', total: 2299, paymentMethod: 'COD',
    items: [
      { id: 18, name: 'Ethnic Palazzo Set', brand: 'W', price: 2299, imageEmoji: '👘', size: 'S', qty: 1 },
    ],
    address: { name: 'Priya Sharma', address: '123 MG Road, Bangalore - 560001' },
  },
];

const STATUS_CONFIG = {
  PROCESSING: { label: 'Order Placed', color: '#FF7C5C', icon: '📦', steps: ['Order Placed', 'Processing', 'Shipped', 'Delivered'] },
  SHIPPED: { label: 'Shipped', color: '#2563EB', icon: '🚚', steps: ['Order Placed', 'Processing', 'Shipped', 'Delivered'] },
  DELIVERED: { label: 'Delivered', color: '#03A685', icon: '✅', steps: ['Order Placed', 'Processing', 'Shipped', 'Delivered'] },
  CANCELLED: { label: 'Cancelled', color: '#FF3F6C', icon: '❌', steps: [] },
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [filter, setFilter] = useState('ALL');

  useEffect(() => {
    api.get('/api/orders/my')
      .then(res => setOrders(res.data || []))
      .catch(() => setOrders(MOCK_ORDERS))
      .finally(() => setLoading(false));
  }, []);

  const getStepIndex = (status) => {
    const map = { PROCESSING: 1, SHIPPED: 2, DELIVERED: 3 };
    return map[status] ?? 0;
  };

  const filteredOrders = orders.filter(o => filter === 'ALL' || o.status === filter);

  if (loading) return <div className="loading-wrapper" style={{ paddingTop: 100 }}><div className="spinner" /><p>Loading orders...</p></div>;

  return (
    <div className="orders-page">
      <div className="orders-layout">
        {/* Sidebar */}
        <aside className="orders-sidebar">
          <h3>MY ORDERS</h3>
          {['ALL', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED'].map(f => (
            <button
              key={f}
              className={`order-filter-btn${filter === f ? ' active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'ALL' ? 'All Orders' : f === 'PROCESSING' ? 'Processing' : f.charAt(0) + f.slice(1).toLowerCase()}
              {filter === f && <span className="active-dot" />}
            </button>
          ))}
        </aside>

        {/* Orders List */}
        <div className="orders-content">
          {filteredOrders.length === 0 ? (
            <div className="empty-state">
              <FiPackage style={{ fontSize: 80, color: '#E0E0E0' }} />
              <h2>No orders found</h2>
              <p>You haven't placed any orders yet. Start shopping!</p>
              <Link to="/products" className="btn btn-primary">Shop Now</Link>
            </div>
          ) : (
            filteredOrders.map(order => {
              const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.PROCESSING;
              const stepIndex = getStepIndex(order.status);
              const isExpanded = expandedOrder === order.id;

              return (
                <div key={order.id} className="order-card">
                  {/* Order Header */}
                  <div className="order-card-header" onClick={() => setExpandedOrder(isExpanded ? null : order.id)}>
                    <div className="order-status-section">
                      <span className="order-status-icon">{config.icon}</span>
                      <div>
                        <span className="order-status-label" style={{ color: config.color }}>{config.label}</span>
                        {order.status === 'DELIVERED' && (
                          <p className="order-status-sub">Your order was delivered on {order.deliveredDate}</p>
                        )}
                        {order.status === 'SHIPPED' && (
                          <p className="order-status-sub">Tracking: {order.trackingId}</p>
                        )}
                        {order.status === 'PROCESSING' && (
                          <p className="order-status-sub">Your order is being processed</p>
                        )}
                      </div>
                    </div>
                    <div className="order-meta">
                      <p className="order-id">Order #{order.id}</p>
                      <p className="order-date">{order.date}</p>
                      <p className="order-total">₹{order.total.toLocaleString()}</p>
                      <p className="order-payment">{order.paymentMethod}</p>
                    </div>
                    <button className="order-expand-btn">{isExpanded ? <FiChevronUp /> : <FiChevronDown />}</button>
                  </div>

                  {/* Order Items Preview */}
                  <div className="order-items-preview">
                    {order.items.map(item => (
                      <div key={item.id} className="order-item-preview">
                        <span className="order-item-emoji">{item.imageEmoji}</span>
                        <div className="order-item-info">
                          <p className="order-item-brand">{item.brand}</p>
                          <p className="order-item-name">{item.name}</p>
                          <p className="order-item-meta">Size: {item.size} | Qty: {item.qty}</p>
                        </div>
                        <div className="order-item-price-col">
                          <span className="order-item-price">₹{item.price.toLocaleString()}</span>
                          {order.status === 'DELIVERED' && (
                            <button className="rate-btn"><FiStar /> Rate</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="order-details">
                      {/* Progress Tracker */}
                      {order.status !== 'CANCELLED' && (
                        <div className="order-tracker">
                          <h4>Order Tracking</h4>
                          <div className="tracker-steps">
                            {['Order Placed', 'Processing', 'Shipped', 'Delivered'].map((step, i) => (
                              <div key={step} className={`tracker-step ${i <= stepIndex ? 'done' : ''}`}>
                                <div className="tracker-step-icon">
                                  {i <= stepIndex ? <FiCheck /> : i}
                                </div>
                                <div className="tracker-step-label">
                                  <span>{step}</span>
                                  {i <= stepIndex && <FiTruck className="tracker-icon" />}
                                </div>
                                {i < 3 && <div className={`tracker-line ${i < stepIndex ? 'done' : ''}`} />}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Delivery Address */}
                      <div className="order-address">
                        <h4>Delivery Address</h4>
                        <p><strong>{order.address.name}</strong></p>
                        <p>{order.address.address}</p>
                      </div>

                      {/* Actions */}
                      <div className="order-actions">
                        {order.status === 'DELIVERED' && (
                          <button className="btn btn-secondary btn-sm">Return / Exchange</button>
                        )}
                        {(order.status === 'PROCESSING' || order.status === 'SHIPPED') && (
                          <button className="btn btn-outline btn-sm">Cancel Order</button>
                        )}
                        <button className="btn btn-outline btn-sm">Need Help?</button>
                        <Link to={`/product/${order.items[0].id}`} className="btn btn-secondary btn-sm">Buy Again</Link>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
